import React, { useId } from "react";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { MM_PER_INCH, numeric } from "./helpers";

export default function AlarmTank({ geometry, dimensions, reading, values }) {
  const theme = useTheme();
  const clipId = useId().replace(/:/g, "");
  const { palette } = theme;
  const depth = numeric(dimensions?.depth);
  const current = geometry ? numeric(reading?.height) : null;
  const full = geometry?.full;
  const height = geometry
    ? Math.max(geometry.sensor, depth || 0, current || 0)
    : depth;
  if (!(height > 0)) {
    return <MDTypography variant="caption">No measurements available for a tank preview.</MDTypography>;
  }
  const y = inches => 242 - inches / height * 195;
  const stamp = reading?.timestamp ? new Date(reading.timestamp) : null;
  const age = stamp && Number.isFinite(stamp.getTime()) ? Date.now() - stamp.getTime() : null;
  const old = age === null || age < 0 || age > 30 * 60 * 1000;
  const marks = [{ key: "floor", height: 0, color: palette.text.main, label: "Empty / floor: 0 in" }];
  if (geometry) {
    marks.push({ key: "full", height: full, color: palette.text.main, label: `Full / max: ${full} in` });
    if (current !== null) {
      const percent = numeric(reading?.percent);
      marks.push({
        key: "current", height: current, color: palette.info.main,
        label: `${old ? "Last known" : "Current"}: ${current.toFixed(2)} in${percent === null ? "" : ` / ${percent}%`}`,
      });
    }
    for (const [key, name, color] of [
      ["min1", "High alarm", palette.error.main],
      ["max1", "Low alarm", palette.warning.main],
    ]) {
      const mm = numeric(values?.[key]);
      const level = mm === null ? null : geometry.sensor - mm / MM_PER_INCH;
      if (mm !== null && mm > 0 && mm <= 65535 && level >= 0 && level <= height) {
        marks.push({ key, height: level, color, label: `${name}: ${level.toFixed(2)} in liquid height` });
      }
    }
  }
  return (
    <MDBox>
      <svg viewBox="0 0 360 275" role="img"
        aria-label={`Tank schematic. ${marks.map(mark => mark.label).join(". ")}`}
        style={{ display: "block", width: "100%", maxWidth: 400, margin: "auto" }}>
        <defs>
          <clipPath id={clipId}><rect x="70" y={y(height)} width="220" height={242 - y(height)} rx="12" /></clipPath>
        </defs>
        <rect x="70" y={y(height)} width="220" height={242 - y(height)} rx="12"
          fill={palette.background.card} stroke={palette.text.main} strokeOpacity=".3" />
        {geometry && current !== null && (
          <rect x="71" y={y(current)} width="218" height={242 - y(current)}
            fill={palette.info.main} opacity={old ? ".12" : ".3"} clipPath={`url(#${clipId})`} />
        )}
        {geometry && (
          <>
            <rect x="164" y={y(geometry.sensor) - 10} width="32" height="10" rx="3" fill={palette.info.main} />
            <text x="180" y={y(geometry.sensor) - 17} textAnchor="middle" fill={palette.text.main} fontSize="12">Sensor</text>
          </>
        )}
        {marks.map(mark => (
          <line key={mark.key} x1="62" x2="298" y1={y(mark.height)} y2={y(mark.height)}
            stroke={mark.color} strokeWidth="2"
            strokeDasharray={mark.key === "current" ? undefined : "6 4"} />
        ))}
        <text x="180" y="265" textAnchor="middle" fill={palette.text.main} fontSize="12">Tank floor</text>
      </svg>
      <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
        {marks.map(mark => (
          <MDBox key={mark.key} display="flex" alignItems="center" gap={1}>
            <MDBox sx={{ width: 14, borderTop: `3px solid ${mark.color}`, flexShrink: 0 }} />
            <MDTypography variant="caption">{mark.label}</MDTypography>
          </MDBox>
        ))}
      </MDBox>
      {!geometry && <MDTypography variant="caption">Tank depth only. Calibration is needed to place fill and alarm lines.</MDTypography>}
      {geometry && current === null && <MDTypography variant="caption">Current liquid height is unavailable.</MDTypography>}
      <MDTypography variant="caption" display="block" mt={1}>
        Schematic, not exact tank shape. Alarm lines preview the requested values.
      </MDTypography>
    </MDBox>
  );
}
